import { useState, useEffect, useCallback } from 'react'
import { studyModulesService } from '../services/studyModules'
import { StudyModule } from '../types/database'
import { useAuth } from './useAuth'

export const useStudyModules = () => {
  const [studyModules, setStudyModules] = useState<StudyModule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()

  // Memoize the fetch function to prevent unnecessary re-renders
  const fetchStudyModules = useCallback(async () => {
    try {
      setLoading(true)
      
      // Only fetch if user is authenticated
      if (!user) {
        setStudyModules([])
        setLoading(false)
        return
      }
      
      const modules = await studyModulesService.getStudyModules()
      setStudyModules(modules)
      setError(null)
    } catch (err) {
      console.error('Error fetching study modules:', err)
      setError('Failed to load study modules')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchStudyModules()
  }, [fetchStudyModules])

  const createStudyModule = async (moduleData: Omit<StudyModule, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    try {
      if (!user) {
        throw new Error('User must be authenticated to create study modules')
      }
      
      const newModule = await studyModulesService.createStudyModule(moduleData, user.id)
      
      // Update local state optimistically
      setStudyModules(prev => [newModule, ...prev])
      
      return newModule
    } catch (err) {
      console.error('Error creating study module:', err)
      throw err
    }
  }

  const updateProgress = async (id: string, progress: number) => {
    try {
      await studyModulesService.updateProgress(id, progress)
      
      // Update local state optimistically
      setStudyModules(prev => 
        prev.map(module => 
          module.id === id 
            ? { 
                ...module, 
                progress, 
                status: progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'not-started'
              }
            : module
        )
      )
    } catch (err) {
      console.error('Error updating progress:', err)
      throw err
    }
  }

  return {
    studyModules,
    loading,
    error,
    refetch: fetchStudyModules,
    createStudyModule,
    updateProgress
  }
}